import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DynamicDocumentService, NotyService } from '@app/services';
import { idColors } from '../../shared/floating-fields-id-colors';
import { Subscription } from 'rxjs';
import { SelectOption } from '@app/shared';
import { CustomSignerInput, DynamicDocumentFieldsTypes } from '@app/models';

@Component({
  selector: 'app-create-edit-fields-form',
  templateUrl: './create-edit-fields-form.component.html',
  styleUrls: ['./create-edit-fields-form.component.scss'],
  standalone: false
})
export class CreateEditFieldsFormComponent implements ModalPublicProperties, OnInit, OnDestroy {
  @Input() existingNames: ModalPublicProperties['existingNames'] = [];
  @Input() editor: any;
  @Output() closeSidebar = new EventEmitter();
  @Output() returnValue = new EventEmitter<any>();

  form!: FormGroup;
  variable?: ModalPublicProperties['variable'];
  fieldToEdit?: HTMLElement;

  signerOptions!: SelectOption[];
  typeOptions!: SelectOption[];

  private formChangesSubscription: Subscription;

  @ViewChild('nameInput', { static: false }) private fieldsFormNameInput: ElementRef<HTMLInputElement>;
  readonly types = DynamicDocumentFieldsTypes;
  readonly idColors = idColors;

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private notyService: NotyService,
    private dynamicDocumentService: DynamicDocumentService
  ) {
    this.form = this.formBuilder.group({
      variable: this.formBuilder.group({
        name: null,
        type: '',
        required: true,
        signer: '0',
        showHelp: false,
        helpText: '',
        regex: '',
        id: ''
      } as ModalPublicProperties['variable'])
    });
  }

  ngOnInit() {
    this.formChangesSubscription = this.form.valueChanges.subscribe({
      next: () => {
        if (!this.variable) return;
        this.autoSaveFieldsForm();
      }
    });

    this.typeOptions = this.getTypesAsSelectOptions();
  }

  ngOnDestroy() {
    this.formChangesSubscription.unsubscribe();
  }

  setSignersAsSelectOptions(signers: CustomSignerInput[]) {
    let options: SelectOption[] = signers.map((signer, index) => {
      return {
        key: index.toString(),
        value: this.translateService.instant('documents.' + (index == 0 ? 'creator' : 'signatorie')) + (index > 0 ? ' ' + index : ''),
        icon: 'fa-solid fa-square',
        iconColor: idColors[index % idColors.length] + '7F'
      };
    });
    this.signerOptions = options;
  }

  getTypesAsSelectOptions(): SelectOption[] {
    let typesAsStringArray: Array<string> = Object.keys(this.types);
    let options: SelectOption[] = typesAsStringArray.map(type => {
      return {
        key: type,
        value: this.translateService.instant('documents.fields.types.' + type)
      };
    });
    return options;
  }

  returnValues() {
    const result: any = this.form.get('variable').value;
    result.name = (result.name || '').trim().replace(/\s+/, ' ');

    if (this.checkRegexIsValid()) {
      this.returnValue.emit(this.formatReturnValues(result));
      if (!this.variable) this.setFieldOptionsFormValues();
    } else {
      this.notyService.error(this.translateService.instant('notyService.invalidRegex'));
    }
  }

  formatReturnValues(result: any) {
    return {
      name: result.name,
      type: result.type,
      signer: result.signer,
      show_help: result.showHelp,
      help_text: result.helpText,
      required: result.required,
      regex: result.regex
    };
  }

  checkRegexIsValid(): boolean {
    let inputRegex = document.querySelector('[formControlName="regex"') as HTMLTextAreaElement;
    try {
      new RegExp(inputRegex.value);
      return true;
    } catch (e) {
      return false;
    }
  }

  deleteField() {
    this.returnValue.emit(null);
    this.closeSidebar.emit();
  }

  setFieldOptionsFormValues(variable?: ModalPublicProperties['variable'], fieldToEdit?: HTMLElement) {
    this.variable = variable;

    this.fieldToEdit = document.querySelector('[data-field-selected]');
    if (this.fieldToEdit && this.fieldToEdit != fieldToEdit) {
      this.dynamicDocumentService.updateInlineFieldAttrs(this.editor, this.fieldToEdit, { selected: 'false' });
    }

    if (fieldToEdit) {
      this.dynamicDocumentService.updateInlineFieldAttrs(this.editor, fieldToEdit, { selected: 'true' });
      this.fieldToEdit = fieldToEdit;
    } else {
      this.fieldToEdit = null;
    }

    this.form.get('variable').setValue({
      name: variable ? variable.name : null,
      type: variable ? variable.type : DynamicDocumentFieldsTypes.shortTextField,
      required: variable ? variable.required : true,
      signer: variable ? variable.signer : '0',
      showHelp: variable ? variable.showHelp : false,
      helpText: variable && variable.showHelp ? variable.helpText : '',
      regex: variable ? variable.regex : '',
      id: variable ? variable.id : ''
    } as ModalPublicProperties['variable']);

    this.form.markAsUntouched();
    setTimeout(() => this.fieldsFormNameInput.nativeElement.focus());
  }

  autoSaveFieldsForm() {
    if (!this.variable) return;
    if (this.form.invalid) {
      this.notyService.error(this.translateService.instant('notyService.fieldOptionsAutoSaveFailed'));
      return;
    }
    this.returnValues();
    this.updateCurrentFieldSpecificData();
    this.updateAllFields();
  }

  updateCurrentFieldSpecificData() {
    this.fieldToEdit = document.querySelector('[data-field-selected]');
    if (!this.fieldToEdit) return;

    const result = this.form.get('variable').value;
    this.dynamicDocumentService.updateInlineFieldAttrs(this.editor, this.fieldToEdit, { id: result.id, signer: result.signer, color: `${result.signer % idColors.length}` });
  }

  updateAllFields() {
    const result = this.form.get('variable').value;
    document.querySelectorAll(`[data-field-name="${this.variable.name}"], [data-field-name="${result.name}"]`).forEach((v: HTMLElement) => {
      if (this.variable.name != result.name) {
        this.dynamicDocumentService.updateInlineFieldAttrs(this.editor, v, { name: result.name });
        v.setAttribute('data-field-name', result.name);
      }
      if (this.variable.type != result.type) {
        this.dynamicDocumentService.updateInlineFieldAttrs(this.editor, v, { type: result.type });
        v.setAttribute('data-field-type', result.type);
      }
    });
    this.variable.name = result.name;
  }

  closeModal() {
    this.fieldToEdit = document.querySelector('[data-field-selected]');
    if (this.fieldToEdit) {
      this.dynamicDocumentService.updateInlineFieldAttrs(this.editor, this.fieldToEdit, { selected: 'false' });
    }
    this.closeSidebar.emit();
  }
}

export interface ModalPublicProperties {
  variable?: {
    name: string;
    type: string;
    required: boolean;
    showHelp: boolean;
    signer: string;
    helpText: string;
    regex: string;
    id: string;
  };
  existingNames?: string[];
}
